import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import FocusTrap from "focus-trap-react";
import styled from "styled-components";
import { breakpointRuleTablet, ColorGrayscale, ZIndex } from "../utilities/variables";
import "/website/assets/scss/components/_modal-backdrop.scss";
const closeSvgIcon = (React.createElement("svg", { className: "btn-close__icon", "aria-hidden": "true", width: "29", height: "28", viewBox: "0 0 29 28", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("rect", { x: "0.5", width: "28", height: "28", rx: "14", fill: "#F6F6F6" }),
    React.createElement("path", { d: "M9 9L19.6452 19.6452", stroke: "black", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }),
    React.createElement("path", { d: "M9 19.6455L19.6452 9.00035", stroke: "black", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })));
const ModalFakeBackdrop = styled(motion.div) `
    z-index: ${ZIndex.modalBackdrop};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 100vh;
    height: -webkit-fill-available;

    display: flex;
    justify-content: center;
    align-items: center;


    &::before {
        display: none;
    }

    @media (${breakpointRuleTablet}) {
        padding: 15px 0;
        overflow: auto;
    }
`;
const ModalContainer = styled(motion.div) `
    display: flex;
    flex-direction: column;
    z-index: ${ZIndex.modal};
    position: absolute;
    bottom: 0;
    left: 50%;

    width: 100vw;
    max-width: 100vw;
    max-height: 97%;
    padding: 0 15px;

    background: ${ColorGrayscale.white};
    border: 1px solid #E6E6E6;
    border-radius: 20px 20px 0 0;

    transform: translateX(-50%);

    @media (${breakpointRuleTablet}) {
        position: static;
        //top: 50vh;
        left: auto;
        bottom: auto;
        margin: auto 0;

        max-height: none;
        width: 790px;
        padding: 36px 48px 80px;

        border-radius: 16px;

        transform: none;
        //transform: translateX(-50%) translateY(-50%);
    }
`;
const ModalHeader = styled.div `
    padding-top: 16px;
    padding-bottom: 24px;
    text-align: right;

    ${({ withPadding }) => withPadding && `
        padding-left: 15px;
        padding-right: 15px;
    `};

    @media (${breakpointRuleTablet}) {
        padding-top: 0;

        ${({ withPadding }) => withPadding && `
            padding-left: 48px;
            padding-right: 48px;
        `};
    }
`;
const ModalContent = styled.div `
    max-height: calc(97vh - 69px);
    width: ${({ fullWidth }) => !fullWidth ? `calc(100vw - ${15 * 2}px)` : '100vw'};
    overflow: auto;

    @media (${breakpointRuleTablet}) {
        overflow: hidden;
        max-height: none;
        width: auto;
        padding: 0 16px;
    }
`;
const ModalButtonClose = styled.button `
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    letter-spacing: 0.01em;

    display: flex;
    align-items: center;

    padding: 0 4px 0 9px;
    margin: 0 -4px 0 auto;
    background: none;
    border: none;
    border-radius: 50px;

    line-height: 16px;

    transition: all 300ms ease-in-out;
    outline: none;

    svg {
        margin-left: 8px;

        rect {
            transition: all 300ms ease-in-out;
        }
    }

    &:hover,
    &:focus {
        background: ${ColorGrayscale.grey3};

        svg {
            rect {
                fill: ${ColorGrayscale.grey3};
            }
        }
    }
`;
export function Modal({ isOpen, onClose, hideHeader = false, children, styles, disableClickOutside, contentRef }) {
    const ModalBackdropTargetClass = 'modal-backdrop-target';
    const ModalBackdropTargetActive = 'modal-backdrop-target--active';
    const modalBackdropTarget = document.querySelector(`.${ModalBackdropTargetClass}`);
    const [modalAnimate, setModalAnimate] = useState();
    const closeBtnRef = useRef(null);
    const onKeyDownInstance = useCallback(({ key, keyCode }) => {
        if (key === 'Escape' || keyCode === 27) {
            onClose();
        }
    }, []);
    const onClickOutside = useCallback(({ target }) => {
        if (target.classList.contains(ModalBackdropTargetClass)) {
            onClose();
        }
    }, []);
    useEffect(() => {
        var _a;
        if (isOpen) {
            modalBackdropTarget.classList.add(ModalBackdropTargetActive);
            document.addEventListener('keydown', onKeyDownInstance);
            if (!disableClickOutside) {
                document.addEventListener('click', onClickOutside);
            }
            (_a = closeBtnRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else {
            modalBackdropTarget.classList.remove(ModalBackdropTargetActive);
            document.removeEventListener('keydown', onKeyDownInstance);
            document.removeEventListener('click', onClickOutside);
        }
    }, [isOpen]);
    useEffect(() => {
        const matchMedia = window.matchMedia(`(${breakpointRuleTablet})`);
        const matchMediaOnChange = ({ matches }) => {
            const animate = matches
                ? { opacity: 1, y: '0', overflow: 'auto' }
                : { opacity: 1, y: '0' };
            setModalAnimate(animate);
        };
        matchMediaOnChange(matchMedia);
        matchMedia.onchange = matchMediaOnChange;
        return () => {
            matchMedia.removeEventListener('change', matchMediaOnChange);
        };
    }, []);
    return (React.createElement(AnimatePresence, null, isOpen && (React.createElement(ModalFakeBackdrop, { className: ModalBackdropTargetClass, initial: { opacity: 0, y: '50%', overflow: 'hidden' }, animate: modalAnimate, exit: { opacity: 0, y: '50%', overflow: 'hidden' }, transition: { type: 'easeInOut', duration: .35 } }, isOpen && (React.createElement(FocusTrap, { focusTrapOptions: {
            clickOutsideDeactivates: true
        } },
        React.createElement(ModalContainer, { role: "dialog", "aria-modal": "true", "aria-label": "Titulo de Modal", style: styles },
            !hideHeader && (React.createElement(ModalHeader, { withPadding: (styles === null || styles === void 0 ? void 0 : styles.paddingLeft) !== undefined && (styles === null || styles === void 0 ? void 0 : styles.paddingRight) !== undefined },
                React.createElement(ModalButtonClose, { type: "button", onClick: () => onClose(), ref: closeBtnRef },
                    "Cerrar",
                    closeSvgIcon))),
            React.createElement(ModalContent, { ref: contentRef, fullWidth: (styles === null || styles === void 0 ? void 0 : styles.paddingLeft) !== undefined && (styles === null || styles === void 0 ? void 0 : styles.paddingRight) !== undefined }, children))))))));
}
